import { useState, useEffect } from 'react'
import { CalendarDays, Plus, FileSpreadsheet } from 'lucide-react'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import ProgramadosTableIndex from './table/Index'
import { useGlobalContext } from '../../../../../../../context/GlobalState'

export default function ListadoProgramados({ onExcel, onNewSingle, onEdit, refreshTrigger = 0 }) {
  const { masterEntityData } = useGlobalContext()
  const masterEntityId = masterEntityData?.id
  const [query, setQuery] = useState('')
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null) // 'header' | null

  // master_entity_id now comes from GlobalContext

  const toggleDropdown = id => {
    setActiveDropdown(prev => (prev === id ? null : id))
  }

  const closeDropdown = () => {
    setTimeout(() => setActiveDropdown(null), 200)
  }

  const handleNewSingleClick = () => {
    setActiveDropdown(null)
    onNewSingle()
  }

  const handleExcelClick = () => {
    setActiveDropdown(null)
    onExcel()
  }

  useEffect(() => {
    if (masterEntityId) {
      fetchDocuments()
    }
  }, [masterEntityId, refreshTrigger])

  const fetchDocuments = async () => {
    if (!masterEntityId) return
    setLoading(true)
    try {
      const api = new FacturaAPI()
      const params = {
        page: 1,
        page_size: 20,
        master_entity_id: masterEntityId
      }
      const { data } = await api.getDocumentsProgramados(params)
      setDocuments(data.results || [])
    } catch (error) {
      console.error('Error fetching scheduled documents:', error)
      setDocuments([])
    } finally {
      setLoading(false)
    }
  }

  const renderEmptyState = () => (
    <div className='rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center'>
      <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-gray-100 bg-gray-50'>
        <CalendarDays className='text-gray-400 mx-auto' />
      </div>

      <h3 className='mt-4 text-lg font-semibold text-gray-900'>No hay documentos programados</h3>
      <p className='mt-1 text-sm text-gray-500'>
        Programa la emisión automática de tus facturas recurrentes
      </p>
    </div>
  )

  return (
    <div className='py-6'>
      <section className='rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_45px_rgba(15,23,42,0.08)]'>
        <div className='p-4 md:p-6'>
          <div className='mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
            <h3 className='text-lg font-semibold text-text'>Facturas programadas</h3>

            <div className='flex flex-wrap items-center justify-end gap-2'>

              <div className='relative'>
                <button
                  type='button'
                  onClick={() => toggleDropdown('header')}
                  onBlur={closeDropdown}
                  className='flex items-center gap-2 rounded-full text-sm px-4 py-2 shadow hover:shadow-lg hover:bg-opacity-90  bg-accent2  text-white transition hover:bg-accent2/90'
                >
                  <Plus size={16} />
                  Nueva programación
                </button>

                {activeDropdown === 'header' && (
                  <div className='absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl'>
                    <button
                      type='button'
                      onMouseDown={event => event.preventDefault()}
                      onClick={handleNewSingleClick}
                      className='flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-accent2/10'
                    >
                      <Plus size={16} className='mt-0.5 text-slate-500' />
                      <span>
                        <span className='block text-sm font-semibold text-slate-900'>
                          Nueva programación
                        </span>
                        <span className='block text-xs text-slate-500'>Programación individual</span>
                      </span>
                    </button>

                    <button
                      type='button'
                      onMouseDown={event => event.preventDefault()}
                      onClick={handleExcelClick}
                      className='flex w-full items-start gap-3 border-t border-slate-100 px-4 py-3 text-left transition hover:bg-accent2/10'
                    >
                      <FileSpreadsheet size={16} className='mt-0.5 text-slate-500' />
                      <span>
                        <span className='block text-sm font-semibold text-slate-900'>
                          Programación con Excel
                        </span>
                        <span className='block text-xs text-slate-500'>Cargar desde Excel</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className='rounded-2xl border border-slate-200 bg-slate-50 p-12 text-center text-sm text-slate-500'>
              Cargando documentos...
            </div>
          ) : documents.length > 0 ? (
            <ProgramadosTableIndex
              documents={documents}
              query={query}
              setQuery={setQuery}
              onEdit={onEdit}
              onDeleteSuccess={fetchDocuments}
            />
          ) : (
            renderEmptyState()
          )}
        </div>
      </section>
    </div>
  )
}
